import { someValueIsNull } from '../../../../../helpers/arrayHelpers';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { parseNumberOrNull } from '../../../../../helpers/stringHelpers';
import { RoomFormModel } from '../../../../shared/quote-or-design-form/rooms/roomFormModel';
import { DesignFormModel } from '../../designFormModel';

export const getDesignMaterialsCalculationsInputs = (formModel: DesignFormModel): DesignMaterialsCalculationsInputs => {
  const rooms = formModel.units
    .flatMap((unit, unitIndex) =>
      unit.manifolds.flatMap((manifold, manifoldIndex) => {
        if (manifold.rooms == null) {
          return null;
        }

        return manifold.rooms.flatMap((room, roomIndex) =>
          getDesignCalculationsInputsForRoom(room, unitIndex, manifoldIndex, roomIndex),
        );
      }),
    )
    .filter(r => r != null) as Array<RoomDesignMaterialsCalculationsInputs>;

  return { rooms };
};

const getDesignCalculationsInputsForRoom = (
  room: RoomFormModel,
  unitIndex: number,
  manifoldIndex: number,
  roomIndex: number,
): RoomDesignMaterialsCalculationsInputs | null => {
  if (room.floorConstruction == null || room.designRoomDetails == null) {
    return null;
  }

  const areaInMetresSquared = parseNumberOrNull(room.areaInMetresSquared);
  const voidAreaInMetresSquared = parseNumberOrNull(room.designRoomDetails.voidAreaInMetresSquared);
  const unheatedAreaInMetresSquared = parseNumberOrNull(room.designRoomDetails.unheatedAreaInMetresSquared);
  const hasPanelsForUnheatedArea = room.designRoomDetails.hasPanelsForUnheatedArea;
  const drawnLengthInMetres = parseNumberOrNull(room.designRoomDetails.drawnLengthInMetres);
  const verticalRunbackInMetres = parseNumberOrNull(room.designRoomDetails.verticalRunbackInMetres);
  const oversupplyLengthInMetres = parseNumberOrNull(room.designRoomDetails.oversupplyLengthInMetres);
  const floorConstructionPartId = room.floorConstruction.partId;

  if (
    someValueIsNull([
      areaInMetresSquared,
      voidAreaInMetresSquared,
      unheatedAreaInMetresSquared,
      drawnLengthInMetres,
      verticalRunbackInMetres,
      oversupplyLengthInMetres,
    ])
  ) {
    return null;
  }

  return {
    unitIndex,
    manifoldIndex,
    roomIndex,
    areaInMetresSquared: assertNotNull(areaInMetresSquared),
    voidAreaInMetresSquared: assertNotNull(voidAreaInMetresSquared),
    unheatedAreaInMetresSquared: assertNotNull(unheatedAreaInMetresSquared),
    hasPanelsForUnheatedArea,
    drawnLengthInMetres: assertNotNull(drawnLengthInMetres),
    verticalRunbackInMetres: assertNotNull(verticalRunbackInMetres),
    oversupplyLengthInMetres: assertNotNull(oversupplyLengthInMetres),
    floorConstructionPartId,
  };
};

export type DesignMaterialsCalculationsInputs = {
  rooms: Array<RoomDesignMaterialsCalculationsInputs>;
};

export type RoomDesignMaterialsCalculationsInputs = {
  unitIndex: number;
  manifoldIndex: number;
  roomIndex: number;
  areaInMetresSquared: number;
  voidAreaInMetresSquared: number;
  unheatedAreaInMetresSquared: number;
  hasPanelsForUnheatedArea: boolean;
  drawnLengthInMetres: number;
  verticalRunbackInMetres: number;
  oversupplyLengthInMetres: number;
  floorConstructionPartId: number;
};
